import * as React from 'react'

import { Box, Button, SelectOption, Text } from '@factorialco/deprecated-design-system'
import {
  CreateAssignmentDocument,
  FetchTimeoffPoliciesDocument,
  UpdateInitialPolicyAssignmentDocument,
  UpdateAssignmentDocument,
} from 'generated/resources/graphql'
import capitalize from 'lodash/capitalize'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import Select from 'modules/core/components/Select'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { BusinessError } from 'modules/core/lib/errors'
import { handleResult } from 'modules/core/lib/handleMutation'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { PolicyTimelineItem } from 'modules/timeoff/store/models/TimeoffPolicy/PolicyTimeline'

interface Props {
  employeeId: string
  timelineItem?: PolicyTimelineItem
  toggleUpsertForm: (value: boolean) => () => void
  fetchTimeline: () => Promise<void>
  isDefaultAssignment: boolean
  startLimitDate?: string
}

const isCreate = (item: PolicyTimelineItem | undefined, assignment: boolean): item is undefined =>
  !item && !assignment

const UpsertAssignment = ({
  employeeId,
  timelineItem,
  toggleUpsertForm,
  fetchTimeline,
  isDefaultAssignment,
  startLimitDate,
}: Props) => {
  const { company } = useCurrentSession()

  const { data } = useQuery(FetchTimeoffPoliciesDocument, {
    variables: {
      companyIds: [company.id],
    },
  })

  const policyOptions: SelectOption[] =
    data?.timeoff.policiesConnection.nodes.map((policy) => {
      return {
        label: policy.name,
        value: policy.id,
      }
    }) ?? []
  const defaultPolicyOption = policyOptions[0]

  const [timeoffPolicyId, setTimeoffPolicyId] = React.useState<string>(
    timelineItem?.timeoff_policy_id ?? defaultPolicyOption?.value ?? ''
  )

  React.useEffect(() => {
    if (!timeoffPolicyId && defaultPolicyOption) {
      setTimeoffPolicyId(defaultPolicyOption.value)
    }
  }, [defaultPolicyOption, timeoffPolicyId])

  const [effectiveAt, setEffectiveAt] = React.useState<string>(
    timelineItem?.start_at || dateToString(moment(), 'backend')
  )

  const trackCreateSubmitEvent = () =>
    track(trackingEvents.timeoff.policyAssignments.createPolicyAssignmentSubmit)

  const trackUpdateSubmitEvent = () =>
    track(trackingEvents.timeoff.policyAssignments.updatePolicyAssignmentSubmit)

  const [createAssignmentMutation] = useMutation(CreateAssignmentDocument, {
    extractorFn: (mutationData) => mutationData.timeoffMutations.createTimeoffPolicyAssignment,
  })

  const [updateAssignmentMutation] = useMutation(UpdateAssignmentDocument, {
    extractorFn: (mutationData) => mutationData.timeoffMutations.updateTimeoffPolicyAssignment,
  })

  const [updateInitialAssignmentMutation] = useMutation(UpdateInitialPolicyAssignmentDocument, {
    extractorFn: (mutationData) =>
      mutationData.timeoffMutations.updateInitialTimeoffPolicyAssignment,
  })

  const throwOnErrors = (handled: {
    success: boolean
    errors?: ReadonlyArray<{ message?: string; messages?: string[] }>
  }) => {
    if (!handled.success && 'errors' in handled) {
      const messages = (handled.errors ?? [])
        .map((e) => ('messages' in e ? (e.messages ?? []).join(', ') : (e.message ?? '')))
        .join('. ')
      throw new BusinessError(messages || i18n.t('errors.generic_error'))
    }
  }

  const onSubmit = async () => {
    const { success } = await asyncAction(async () => {
      if (isCreate(timelineItem, isDefaultAssignment)) {
        trackCreateSubmitEvent()
        const result = await createAssignmentMutation({
          variables: {
            employeeId,
            timeoffPolicyId,
            effectiveAt,
          },
        })
        throwOnErrors(handleResult(result, () => {}))
      } else {
        trackUpdateSubmitEvent()

        if (isDefaultAssignment) {
          const result = await updateInitialAssignmentMutation({
            variables: {
              employeeId,
              timeoffPolicyId,
            },
          })

          throwOnErrors(handleResult(result, () => {}))
          return
        }

        const policyAssignmentId = timelineItem?.policy_assignment_id
        if (!policyAssignmentId) {
          throw new BusinessError(i18n.t('errors.generic_error'))
        }

        const result = await updateAssignmentMutation({
          variables: {
            id: policyAssignmentId,
            effectiveAt,
            timeoffPolicyId,
          },
        })

        throwOnErrors(handleResult(result, () => {}))
      }
    })

    if (success) {
      toggleUpsertForm(false)()
      await fetchTimeline()
    }
  }

  return (
    <Box paddingX="s32" paddingY="s24" gap="s16" background="grey100">
      <Box paddingTop="s8">
        <Text size="400" color="grey1000" weight="medium">
          {isCreate(timelineItem, isDefaultAssignment)
            ? i18n.t('time_off.policy_timeline.create_assignment.title')
            : i18n.t('time_off.policy_timeline.edit_assignment.title')}
        </Text>
      </Box>

      <Text size="300">
        {isCreate(timelineItem, isDefaultAssignment)
          ? i18n.t('time_off.policy_timeline.create_assignment.description')
          : i18n.t('time_off.policy_timeline.edit_assignment.description')}
      </Text>
      <Box paddingTop="s16" maxWidth="s400">
        <Select
          label={i18n.t('common.policy')}
          value={timeoffPolicyId?.toString()}
          onChange={(value) => setTimeoffPolicyId(String(value))}
          options={policyOptions}
        />
      </Box>

      {!isDefaultAssignment && (
        <Box paddingTop="s16">
          <DayPicker
            label={capitalize(i18n.t('common.starting_from'))}
            value={effectiveAt}
            valueFormat="backend"
            onChange={(value) => setEffectiveAt(value)}
            minDate={startLimitDate ? moment(startLimitDate) : undefined}
          />
        </Box>
      )}

      <Box alignItems="flexEnd">
        <Box flexDirection="row" gap="s16">
          <Button onClick={toggleUpsertForm(false)} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button onClick={onSubmit}>
            {isCreate(timelineItem, isDefaultAssignment)
              ? i18n.t('time_off.policy_timeline.create_assignment.submit')
              : i18n.t('time_off.policy_timeline.edit_assignment.submit')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default UpsertAssignment
